import type { createTheme } from '@mui/material/styles'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { ThemeProvider as JSSThemeProvider } from '@mui/styles'
import type { PropsWithChildren } from 'react'

interface DualThemeProviderProps {
  theme: ReturnType<typeof createTheme>
}

/**
 * DualThemeProvider is a temporary bridge component for apps that still use legacy
 * @mui/styles (e.g. makeStyles, makeStylesFast) alongside modern MUI v5+ components.
 *
 * MUI v5+ uses emotion for styling, while @mui/styles relies on JSS.
 * These systems are not compatible with each other and require separate ThemeProviders.
 *
 * This component ensures both systems receive the same theme by wrapping children in:
 * - MUI's ThemeProvider (for sx/styled/emotion-based components)
 * - @mui/styles ThemeProvider (for legacy JSS-based components)
 *
 * Once all usage of @mui/styles is removed, we can safely delete this and revert
 * to using only the MUI ThemeProvider.
 */
export function DualThemeProvider({ children, theme }: PropsWithChildren<DualThemeProviderProps>) {
  return (
    <MuiThemeProvider theme={theme}>
      <JSSThemeProvider theme={theme}>{children}</JSSThemeProvider>
    </MuiThemeProvider>
  )
}
